import {
  EMAIL_ADDRESS_REGEX,
  PASSWORD_SYMBOL_REGEX,
} from "@/components/activation/Constants";
import {
  DEFAULT_EXAMINEE_SIGN_IN_NEXT_PATH,
  resolveExamineeSignInNextPath,
  useExamineeSignInNextPath,
} from "@/components/activation/signins";
import { useMyPageAPI } from "@/components/examinee/mypage/MyPageAPI";
import { NonFieldError, SuccessAlert } from "@/components/common/SCAlert";
import { SCLink } from "@/components/common/SCLink";
import { TextForm } from "@/components/sleep_checkup_v1/TextForm";
import {
  CognitoAuthenticationResultStore,
  CognitoRefreshTokenStore,
  SleepCheckupUserStore,
  callUserTypeApi,
} from "@/utils/auth";
import { getApiServerUrl, getAxios } from "@/utils/axios";
import { Button, Stack, Typography } from "@mui/material";
import axios, { AxiosResponse } from "axios";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// このページが受け取るstateの型
export type StateType = {
  successMessage: string;
};

export const SignIn = () => {
  const API_URL = getApiServerUrl();
  const location = useLocation();
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const { successMessage } = (location.state as StateType) || {};
  const nextPath = useExamineeSignInNextPath();
  const myPageAPI = useMyPageAPI();

  /**
   * ログイン成功後の遷移先を決める。
   * next が明示されていればそのまま。未指定（既定パス）のときはマイページに表示できる測定が
   * あればマイページ、無ければ（API エラー時も）従来の既定パスへ。
   */
  const resolveNextPathAfterSignIn = (): Promise<string> => {
    if (nextPath !== DEFAULT_EXAMINEE_SIGN_IN_NEXT_PATH) {
      // next が明示されている場合は API を呼ばずそのまま遷移する
      return Promise.resolve(nextPath);
    }
    return myPageAPI
      .getMeasurements()
      .then((res) => res.data.results.length > 0)
      .catch(() => false)
      .then((hasMeasurements) =>
        resolveExamineeSignInNextPath(nextPath, hasMeasurements)
      );
  };

  const validators: Record<string, () => string[] | null> = {
    // validatorは引数を受け取らず実行され、エラーが無ければ何もreturnしない。
    // エラーがあった場合はエラーメッセージの配列を返す。
    email: () => {
      if (!email.match(EMAIL_ADDRESS_REGEX)) {
        return ["※メールアドレスの形式が正しくありません"];
      }
      return null;
    },
    passwordNotEmpty: () => {
      if (!password) {
        return ["NG"]; // この返り値は使われない
      }
      return null;
    },
    password: () => {
      if (
        password.replace(PASSWORD_SYMBOL_REGEX, "").replace(/[a-zA-Z\d]/g, "")
      ) {
        return ["※使用できない文字列が含まれています"];
      }
      return null;
    },
  };

  const handleClickLogin = () => {
    if (Object.values(validators).some((v) => v())) {
      // 何かしらのエラーがある場合はログイン処理を行わない
      return;
    }

    axios
      .post(`${API_URL}/api/initiate_auth/`, {
        username: email,
        password: password,
      })
      .then((res: AxiosResponse) => {
        // ChallengeNameが無い場合、認証が成功している
        CognitoAuthenticationResultStore.setItem(res.data.AuthenticationResult);
        CognitoRefreshTokenStore.setItem({
          RefreshToken: res.data.AuthenticationResult.RefreshToken,
        });
        return callUserTypeApi(
          getAxios(API_URL),
          res.data.AuthenticationResult.IdToken
        )
          .then((res: AxiosResponse) => {
            SleepCheckupUserStore.setItem(res.data);
            return resolveNextPathAfterSignIn();
          })
          .then((path) => {
            navigate(path);
          });
      })
      .catch((err: any) => {
        const statusCode = err.response?.status;
        const messages = err.response?.data?.error_messages;
        if (
          statusCode &&
          400 <= statusCode &&
          statusCode < 500 &&
          messages &&
          messages.length > 0
        ) {
          // 400系エラーでエラーメッセージが存在する場合はそれを表示する
          setErrorMessage(messages[0]);
        } else {
          setErrorMessage(
            "エラーが発生しました。申し訳ありませんが、少し時間をおいてから再度お試しください。"
          );
        }

        // エラーアラートが画面内に表示されるようにスクロールをリセットする
        window.scrollTo(0, 0);
      });
  };

  return (
    <>
      <Stack spacing={8} sx={{ mt: 8, mb: 12, mx: 4 }}>
        <Stack spacing={6}>
          <Typography variant="h6" align="center">
            ログイン
          </Typography>

          {/* パスワード再設定直後に表示されるメッセージ */}
          <SuccessAlert>{successMessage}</SuccessAlert>

          {/* エラーメッセージ */}
          <NonFieldError>{errorMessage}</NonFieldError>

          <TextForm
            title="メールアドレス"
            placeholder="例：sleep-compass@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            validator={validators.email}
          />
          <TextForm
            title="パスワード"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            type="password"
            givenErrors={validators.password()}
          />
        </Stack>
      </Stack>
      <Stack width="100%" sx={{ mb: 16 }}>
        <Button
          variant="contained"
          size="large"
          sx={{ mx: 4, mb: 4 }}
          onClick={handleClickLogin}
          disabled={Object.values(validators).some((v) => v())}
        >
          ログイン
        </Button>
        <SCLink
          variant="body2"
          to={`/examinee/forgot_password?${new URLSearchParams({ next: nextPath })}`}
          sx={{ color: "text.secondary" }}
        >
          パスワードを忘れた場合
        </SCLink>
      </Stack>
    </>
  );
};
